import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import { AlbumFill, CardFill, PlusCircle, XmarkCircle2color } from '@peacecover/icon';
import ChatInputProfileItem from '../../components/chat/ChatInputProfileItem';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import AddPhotoCardBottomSheet from '../../../components/bottomSheet/community/AddPhotoCardBottomSheet';
import PhotoCardContent from './PhotoCardContent';
import { useCreateLoungeMessage } from '../../../domains/lounge/ui/hooks/useCreateLoungeMessage';
import { resizeToBase64 } from '../../../utils/resizeImage';
import Bubble from '../../components/floating/Bubble';
import type { components } from '../../../types/schema';
import saveData from '../../../utils/saveData';

type PhotoCard = components['schemas']['PhotoCard'];

interface LoungeMessageInputProps {
  profileImageUrl: string;
  hasAnonymousBubble: MutableRefObject<boolean>;
  onActiveChange?: (active: boolean) => void; // 입력중/첨부 존재 여부
  categoryGroup?: string;
}

const LoungeMessageInput = ({
  profileImageUrl,
  hasAnonymousBubble,
  onActiveChange,
  categoryGroup
}: LoungeMessageInputProps) => {
  const [isShowAddPhotoCardBottomSheet, setIsShowAddPhotoCardBottomSheet] = useState(false);
  const [lineCount, setLineCount] = useState(1);
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [photoCard, setPhotoCard] = useState<PhotoCard | null>(null);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(
    sessionStorage.getItem('isAnonymousInLounge') === 'true'
  );
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { mutateAsync: createLoungeMessage, isLoading } = useCreateLoungeMessage();

  const MAX_ROWS = 3;
  const LINE_HEIGHT_PX = 18; // 라인 높이 기준값

  const isMultiline = lineCount >= 2;

  const adjustTextareaSize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    const maxHeight = LINE_HEIGHT_PX * MAX_ROWS;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    const estimatedLines = Math.max(1, Math.round(el.scrollHeight / LINE_HEIGHT_PX));
    setLineCount(estimatedLines);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adjustTextareaSize is dependent on message
  useEffect(() => {
    requestAnimationFrame(adjustTextareaSize);
  }, [message]);

  // prevent showing bubble if user has ever enabled anonymous before
  // biome-ignore lint/correctness/useExhaustiveDependencies: hasAnonymousBubble is dependent on localStorage
  useEffect(() => {
    const used = localStorage.getItem('loungeHasUsedAnonymous');
    if (used === 'true') {
      hasAnonymousBubble.current = false;
    }
  }, []);

  // 입력중/첨부 상태 변화 감지하여 부모에 전달
  useEffect(() => {
    const active = !!message.trim() || images.length > 0 || !!photoCard || isMenuOpen;
    onActiveChange?.(active);
  }, [message, images.length, photoCard, isMenuOpen, onActiveChange]);

  const handleAlbumClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList) return;
    const currentLength = images.length;
    const maxAdd = Math.max(0, 4 - currentLength);
    if (maxAdd === 0) return;
    const selectedFiles = Array.from(fileList).slice(0, maxAdd);
    const dataUrls = await Promise.all(selectedFiles.map((file) => resizeToBase64(file)));
    setImages((prev) => [...prev, ...dataUrls]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsMenuOpen(false);
  };

  const onSelectedPhotoCard = (photoCard: PhotoCard) => {
    setPhotoCard(photoCard);
    setIsShowAddPhotoCardBottomSheet(false);
    setIsMenuOpen(false);
  };

  const handleCreateLoungeMessage = async () => {
    if (!message.trim() && images.length === 0 && !photoCard) return;
    const requestBody = {
      post: {
        title: '',
        content: message,
        category: categoryGroup === 'community' ? 'FreeTalk' : categoryGroup,
        post_type: 'Post',
        photo_card_id: photoCard ? photoCard.photoCardId : '',
        location_id: '',
        event_id: '',
        artist_id: photoCard?.artistId ?? undefined,
        parent_id: '',
        repost_source_id: '',
        anonymous: isAnonymous,
        display_on_feed: true,
        data_urls: images || []
      }
    };
    try {
      const response = await createLoungeMessage(requestBody);
      if (response.post) {
        setMessage('');
        setImages([]);
        setPhotoCard(null);
      } else {
        throw new Error('Failed to create lounge message');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAnonymousClick = () => {
    const next = !isAnonymous;
    setIsAnonymous(next);
    sessionStorage.setItem('isAnonymousInLounge', next ? 'true' : 'false');
    hasAnonymousBubble.current = false;
    if (next) {
      saveData('loungeHasUsedAnonymous', 'true');
    }
  };

  const disabled = isLoading || (!message.trim() && images.length === 0);
  const hasUsedAnonymous = localStorage.getItem('loungeHasUsedAnonymous') === 'true';

  return (
    <>
      <div
        className={`flex flex-col bg-pure-white dark:bg-background-elevated pb-[env(safe-area-inset-bottom)] w-full shadow-[0_-2px_2px_0_rgba(0,0,0,0.02),_0_-3px_8px_0_rgba(0,0,0,0.03)] dark:shadow-[0_-3px_8px_0_rgba(0,0,0,0.54),_0_-2px_2px_0_rgba(0,0,0,0.42)] ${isMenuOpen ? 'rounded-t-[20px]' : ''}`}
      >
        {isMenuOpen && (
          <>
            <InputMenuBox
              handleAlbumClick={handleAlbumClick}
              handleAddPhotoCardClick={() => setIsShowAddPhotoCardBottomSheet(true)}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFilesSelected(e.target.files)}
              className="hidden"
            />
          </>
        )}
        <div
          className={`flex ${isMultiline ? 'items-end' : 'items-center'} gap-[4px] w-full px-[10px] pt-[8px] pb-[10px]`}
        >
          {images.length === 0 && !photoCard && (
            <button
              type="button"
              className={`w-[36px] h-[36px] ${isMultiline ? 'mb-[8px]' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <PlusCircle
                size={36}
                className={`text-text-main-lv2 ${isMenuOpen ? 'rotate-45' : ''} transition-transform duration-300`}
              />
            </button>
          )}
          <div
            className={`w-full flex flex-col bg-secondary-light dark:bg-secondary-dark relative ${isMultiline ? 'rounded-[20px]' : 'rounded-[30px]'} ${images.length > 0 ? 'overflow-hidden' : ''}`}
          >
            {images.length > 0 && (
              <HorizontalScroll>
                <div className="flex gap-[8px] overflow-x-scroll px-[12px] pt-[12px] pb-[8px]">
                  {images.map((url) => (
                    <AttachedImage
                      key={url}
                      url={url}
                      onRemove={() => setImages(images.filter((image) => image !== url))}
                    />
                  ))}
                </div>
              </HorizontalScroll>
            )}
            {photoCard && (
              <AttachedPhotoCard photoCard={photoCard} onRemove={() => setPhotoCard(null)} />
            )}
            <div
              className={`flex gap-[3px] w-full ${
                isMultiline ? 'px-[6px] py-[8px] items-end' : 'px-[8px] py-[6px] items-center'
              } `}
            >
              <ChatInputProfileItem
                isAnonymous={isAnonymous}
                thumbnailUrl={profileImageUrl}
                onClick={handleAnonymousClick}
              />
              <textarea
                placeholder={
                  isAnonymous
                    ? (t('LoungeMessageInput.anonymousPlaceholder') as string)
                    : (t('LoungeMessageInput.placeholder') as string)
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                ref={textareaRef}
                rows={1}
                className="bg-transparent w-full font-medium text-[15px] leading-[18px] resize-none outline-none placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark [&::-webkit-scrollbar]:hidden"
              />
              <button
                type="button"
                className="p-[5.5px] shrink-0 inline-flex items-center justify-center"
                disabled={disabled}
                onClick={handleCreateLoungeMessage}
              >
                <img
                  src={`/assets/icons/${disabled ? 'disabled-up' : 'up'}.svg`}
                  alt="send"
                  className="w-[25px] h-[25px] flex-none"
                />
              </button>
              {!isMenuOpen && hasAnonymousBubble.current && !hasUsedAnonymous && (
                <div className="absolute top-[-33px]">
                  <Bubble text={t('LoungeMessageInput.anonymousBubble')} gradientDirection="left" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isShowAddPhotoCardBottomSheet && (
        <AddPhotoCardBottomSheet
          title={t('addPostPage.addPhotoCard')}
          isCheckPhotoCardBottomSheetOpen={isShowAddPhotoCardBottomSheet}
          setIsCheckPhotoCardBottomSheetOpen={setIsShowAddPhotoCardBottomSheet}
          onConfirmedPhotoCards={onSelectedPhotoCard}
        />
      )}
    </>
  );
};

export default LoungeMessageInput;

const InputMenuBox = ({
  handleAlbumClick,
  handleAddPhotoCardClick
}: {
  handleAlbumClick: () => void;
  handleAddPhotoCardClick: () => void;
}) => {
  return (
    <div className="px-[16px] pt-[15px] pb-[6px] flex items-center gap-[8px]">
      <Button
        size="small"
        className="flex items-center gap-[4px] px-[12px] bg-surface-lv2"
        onClick={handleAlbumClick}
      >
        <AlbumFill size={14} className="text-text-main-lv2" />
        <p className="text-[13px] font-medium text-text-main-lv2 leading-[16px]">
          {t('LoungeMessageInput.attachMenu.photo')}
        </p>
      </Button>
      <Button
        size="small"
        className="flex items-center gap-[4px] px-[12px] bg-surface-lv2"
        onClick={handleAddPhotoCardClick}
      >
        <CardFill size={14} className="text-text-main-lv2" />
        <p className="text-[13px] font-medium text-text-main-lv2 leading-[16px]">
          {t('LoungeMessageInput.attachMenu.poca')}
        </p>
      </Button>
    </div>
  );
};

const AttachedImage = ({ url, onRemove }: { url: string; onRemove: () => void }) => {
  return (
    <div className="relative">
      <div className="flex-shrink-0 w-[182px] h-[96px] rounded-[16px] overflow-hidden">
        <img src={url} alt="" className="w-full h-full object-cover" />
      </div>
      <button type="button" className="absolute top-[-10px] right-[-10px] z-10" onClick={onRemove}>
        <XmarkCircle2color size={26} className="text-text-main-lv3 [&_#Subtract]:fill-pure-white" />
      </button>
    </div>
  );
};

const AttachedPhotoCard = ({
  photoCard,
  onRemove
}: {
  photoCard: PhotoCard;
  onRemove: () => void;
}) => {
  return (
    <div className="px-[12px] pt-[12px] pb-[8px]">
      <div className="relative w-fit">
        <PhotoCardContent photoCard={photoCard} />
        <button
          type="button"
          className="absolute top-[-10px] right-[-10px] z-10"
          onClick={onRemove}
        >
          <XmarkCircle2color
            size={26}
            className="text-text-main-lv3 [&_#Subtract]:fill-pure-white"
          />
        </button>
      </div>
    </div>
  );
};
